import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * LA IA DEL NÚCLEO — los endpoints de administración (`/api/ia/admin/*`).
 *
 * Contrato: `kredit-api-MT/modules/ia/CONTRATO.md` §7.2. Ese archivo manda: si
 * una firma de acá no coincide con la de allá, se corrige allá primero.
 *
 * 📍 Mismo molde que `MerchantService`: base `environment.BASE_LA`, métodos
 * como propiedades de flecha y `lastValueFrom`. El token lo pone el
 * `AuthInterceptorService` en la cabecera `authorization` (sin `Bearer`), igual
 * que en el resto de la consola: acá no se toca.
 *
 * 🔴 Todas las respuestas vienen envueltas: `{ ok:true, data }` o
 * `{ ok:false, error }`. `pedir()` desenvuelve: devuelve `data`, y si el
 * servidor dijo `ok:false` con un 200, lo convierte en un rechazo con la misma
 * forma que un error HTTP, para que cada pantalla lo maneje en un solo lugar.
 *
 * 🔴 Una clave de proveedor viaja UNA vez, en `guardarCredencial`, y nunca se
 * guarda ni se registra de este lado. El servidor devuelve sólo `ultimos4`.
 */

export type Proveedor = 'google' | 'anthropic' | 'openai';
export type EstadoRegistro = 'OK' | 'FALLA' | 'OMITIDO';
export type TipoRegistro = 'llamada' | 'caso' | 'prueba';
export type CausaApagado = 'MANTENIMIENTO' | 'COSTO' | 'INCIDENTE' | 'REVISION';

export interface ApagadoIA {
  apagado: boolean;
  causa?: string | null;
  motivo?: string | null;
  desde?: string | null;
  por?: string | null;
}

export interface ProveedorEstado {
  configurado: boolean;
  origen: 'comercio' | 'consola' | 'entorno' | null;
  ultimos4?: string | null;
  operativo: boolean;
  ultimoError?: string | null;
}

export interface AgenteEstado {
  habilitado: boolean;
  modeloId: string | null;
  operativo: boolean;
  ultimoError?: string | null;
  gastoHoyUsd: number;
}

/** `estadoIA()` del motor (§4). */
export interface EstadoIA {
  apagado: ApagadoIA;
  claveMaestra: boolean;
  modeloPorOmision: string;
  topeDiarioUsd: number;
  gastoHoyUsd: number;
  proveedores: Partial<Record<Proveedor, ProveedorEstado>>;
  agentes: Record<string, AgenteEstado>;
}

export interface ModeloCatalogo {
  id: string;
  proveedor: Proveedor;
  nombre: string;
  precioEntrada: number;
  precioSalida: number;
  respaldo?: string | string[] | null;
}

export interface Credencial {
  proveedor: Proveedor;
  /** `'plataforma'` o el código del comercio. */
  alcance: string;
  ultimos4: string;
  cargadaPor?: string;
  cargadaEl?: string;
}

export interface PruebaCredencial {
  ok: boolean;
  modeloId?: string;
  latenciaMs?: number;
  error?: string;
}

export interface AgenteConfig {
  agente: string;
  habilitado: boolean;
  modeloId: string | null;
  maxTokens?: number;
  promptExtra?: string;
  topeDiarioUsd?: number | null;
  actualizadoPor?: string;
  updatedAt?: string;
}

export interface CambioAgente {
  habilitado?: boolean;
  modeloId?: string | null;
  maxTokens?: number;
  promptExtra?: string;
  topeDiarioUsd?: number | null;
}

export interface ComercioIA {
  codigo: string;
  nombre: string;
  habilitado: boolean;
  topeDiarioUsd: number | null;
  gastoHoyUsd: number;
  preguntasHoy: number;
  preguntas7d: number;
  credencialPropia: boolean | string | null;
  actualizadoPor?: string | null;
  actualizadoEl?: string | null;
  notas?: string | null;
}

/** Una fila de `ia_decisiones` (§5). */
export interface RegistroIA {
  _id?: string;
  agente: string;
  tipo: TipoRegistro;
  traceId: string;
  estado: EstadoRegistro;
  motivo?: string | null;
  comercio?: string | null;
  usuario?: string | null;
  pantalla?: string | null;
  pregunta?: string | null;
  intencion?: string | null;
  peticion?: any;
  respuesta?: any;
  filas?: number | null;
  fichas?: string[];
  herramientas?: string[];
  modeloId?: string | null;
  proveedor?: string | null;
  tokensEntrada?: number | null;
  tokensSalida?: number | null;
  costoUsd?: number | null;
  latenciaMs?: number | null;
  humanoDeAcuerdo?: boolean | null;
  humanoPor?: string | null;
  humanoEl?: string | null;
  creadoEl: string;
}

export interface ComercioIADetalle extends ComercioIA {
  ultimas: RegistroIA[];
}

export interface FiltrosBitacora {
  agente?: string;
  estado?: EstadoRegistro;
  tipo?: TipoRegistro;
  comercio?: string;
  desde?: string;
  hasta?: string;
  limite?: number;
  salto?: number;
}

export interface ResumenIA {
  porAgente: any[];
  porComercio: any[];
  totalUsd: number;
}

export interface TablaRespuesta {
  titulo: string;
  columnas: string[];
  filas: (string | number | null)[][];
  truncado: boolean;
}

/** `RespuestaAsistente` (§6.4), más lo que agrega `/admin/probar`. */
export interface RespuestaAsistente {
  respuesta: string;
  tipo: 'cifras' | 'guia' | 'ficha' | 'vivo' | 'nose';
  tabla: TablaRespuesta | null;
  pendientes: { que: string; estado: 'falta' | 'ok' | 'pedido'; donde: string | null }[] | null;
  fichas: { id: string; titulo: string }[];
  enlaces: { destino: string; label: string }[];
  sugerencias: string[];
  noSe: boolean;
  traceId: string;
  decisionId: string;
  modeloId?: string | null;
  costoUsd?: number | null;
}

export interface TurnoHistorial {
  rol: 'usuario' | 'asistente';
  texto: string;
}

@Injectable({ providedIn: 'root' })
export class IaService {
  private baseUrl: string;
  private api: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
    this.api = `${this.baseUrl}/api/ia/admin`;
  }

  private json = { headers: { 'content-type': 'application/json' } };

  /**
   * El último estado que se leyó, para la cabecera de «Inteligencia artificial».
   * La cinta de «la IA está apagada» vive arriba de las solapas; cuando la
   * solapa Estado apaga o prende, la cinta se entera sin pedir otra vez.
   */
  readonly ultimoEstado$ = new BehaviorSubject<EstadoIA | null>(null);

  /** Desenvuelve `{ ok, data }`. Un `ok:false` con 200 se rechaza igual que un 4xx. */
  private pedir = async <T>(obs: Observable<any>): Promise<T> => {
    const r = await lastValueFrom(obs);
    if (r && r.ok === false) {
      throw { status: 200, error: r };
    }
    return (r && Object.prototype.hasOwnProperty.call(r, 'data') ? r.data : r) as T;
  };

  /** Los parámetros de consulta, sin los vacíos. */
  private params = (p: { [k: string]: any }): HttpParams => {
    let hp = new HttpParams();
    Object.keys(p || {}).forEach((k) => {
      const v = p[k];
      if (v !== undefined && v !== null && v !== '') hp = hp.set(k, String(v));
    });
    return hp;
  };

  // ── Estado general, interruptor y ajustes ─────────────────────────────────

  /**
   * 🔴 UNA SOLA IDA POR CARGA. La carcasa (para su cinta) y la solapa que se abre (para su
   * contenido) piden el estado a la vez; mientras una petición está en vuelo, la segunda recibe
   * la MISMA promesa en vez de salir otra vez al servidor. Terminada, la próxima vuelve a pedir.
   */
  private estadoEnVuelo: Promise<EstadoIA> | null = null;

  getEstado = (): Promise<EstadoIA> => {
    if (this.estadoEnVuelo) return this.estadoEnVuelo;
    const p = this.pedir<EstadoIA>(this.httpClient.get<any>(`${this.api}/estado`))
      .then((e) => { this.ultimoEstado$.next(e); return e; })
      .finally(() => { this.estadoEnVuelo = null; });
    this.estadoEnVuelo = p;
    return p;
  };

  /** Apagar pide `APAGAR` en la pantalla; prender no pide nada. */
  cambiarApagado = async (apagado: boolean, causa?: CausaApagado, motivo?: string): Promise<ApagadoIA> =>
    this.pedir<ApagadoIA>(this.httpClient.patch<any>(`${this.api}/apagado`,
      { apagado, ...(causa ? { causa } : {}), ...(motivo ? { motivo } : {}) }, this.json));

  guardarAjustes = async (cambios: { modeloPorOmision?: string; topeDiarioUsd?: number }): Promise<any> =>
    this.pedir<any>(this.httpClient.patch<any>(`${this.api}/ajustes`, cambios, this.json));

  getCatalogo = async (): Promise<ModeloCatalogo[]> =>
    this.pedir<ModeloCatalogo[]>(this.httpClient.get<any>(`${this.api}/catalogo`));

  // ── Credenciales (de plataforma, o de un comercio con `comercio`) ─────────

  getCredenciales = async (): Promise<Credencial[]> =>
    this.pedir<Credencial[]>(this.httpClient.get<any>(`${this.api}/credenciales`));

  guardarCredencial = async (proveedor: Proveedor, clave: string, comercio?: string): Promise<Credencial> =>
    this.pedir<Credencial>(this.httpClient.put<any>(`${this.api}/credenciales/${proveedor}`,
      { clave, ...(comercio ? { comercio } : {}) }, this.json));

  quitarCredencial = async (proveedor: Proveedor, comercio?: string): Promise<boolean> =>
    this.pedir<boolean>(this.httpClient.delete<any>(`${this.api}/credenciales/${proveedor}`,
      { params: this.params({ comercio }) }));

  probarCredencial = async (proveedor: Proveedor, comercio?: string): Promise<PruebaCredencial> =>
    this.pedir<PruebaCredencial>(this.httpClient.post<any>(`${this.api}/credenciales/${proveedor}/probar`,
      comercio ? { comercio } : {}, this.json));

  // ── Agentes ───────────────────────────────────────────────────────────────

  getAgentes = async (): Promise<AgenteConfig[]> =>
    this.pedir<AgenteConfig[]>(this.httpClient.get<any>(`${this.api}/agentes`));

  /** `modeloId: null` = volver al modelo por omisión. */
  guardarAgente = async (agente: string, cambios: CambioAgente): Promise<AgenteConfig> =>
    this.pedir<AgenteConfig>(this.httpClient.patch<any>(`${this.api}/agentes/${encodeURIComponent(agente)}`,
      cambios, this.json));

  // ── Comercios ─────────────────────────────────────────────────────────────

  /** TODOS los comercios; los que no tienen documento vienen `habilitado:false`. */
  getComercios = async (q?: string): Promise<ComercioIA[]> =>
    this.pedir<ComercioIA[]>(this.httpClient.get<any>(`${this.api}/comercios`, { params: this.params({ q }) }));

  getComercio = async (codigo: string): Promise<ComercioIADetalle> =>
    this.pedir<ComercioIADetalle>(this.httpClient.get<any>(`${this.api}/comercios/${encodeURIComponent(codigo)}`));

  /** `topeDiarioUsd: null` = sin tope propio (manda el de plataforma). */
  guardarComercio = async (codigo: string,
                           cambios: { habilitado?: boolean; topeDiarioUsd?: number | null; notas?: string }): Promise<any> =>
    this.pedir<any>(this.httpClient.patch<any>(`${this.api}/comercios/${encodeURIComponent(codigo)}`,
      cambios, this.json));

  // ── Bitácora y resumen ────────────────────────────────────────────────────

  getBitacora = async (f: FiltrosBitacora): Promise<{ total: number; filas: RegistroIA[] }> =>
    this.pedir<{ total: number; filas: RegistroIA[] }>(
      this.httpClient.get<any>(`${this.api}/bitacora`, { params: this.params(f as any) }));

  getResumen = async (dias = 7, comercio?: string): Promise<ResumenIA> =>
    this.pedir<ResumenIA>(this.httpClient.get<any>(`${this.api}/resumen`, { params: this.params({ dias, comercio }) }));

  opinarDecision = async (id: string, deAcuerdo: boolean): Promise<boolean> =>
    this.pedir<boolean>(this.httpClient.post<any>(`${this.api}/decisiones/${encodeURIComponent(id)}/acuerdo`,
      { deAcuerdo }, this.json));

  // ── Probar, examen y banco de casos ───────────────────────────────────────

  /** Corre como el administrador de ESE comercio, aunque el comercio esté apagado. */
  probar = async (comercio: string, pregunta: string, historial?: TurnoHistorial[]): Promise<RespuestaAsistente> =>
    this.pedir<RespuestaAsistente>(this.httpClient.post<any>(`${this.api}/probar`,
      { comercio, pregunta, ...(historial && historial.length ? { historial } : {}) }, this.json));

  examen = async (modo: 'sinModelo' | 'real'): Promise<{ aprobadas: number; total: number; fallas: any[] }> =>
    this.pedir<any>(this.httpClient.post<any>(`${this.api}/examen`, { modo }, this.json));

  getCasos = async (estado?: string): Promise<any[]> =>
    this.pedir<any[]>(this.httpClient.get<any>(`${this.api}/casos`, { params: this.params({ estado }) }));

  guardarCaso = async (id: string, cambios: { esperado?: any; estado?: string }): Promise<any> =>
    this.pedir<any>(this.httpClient.patch<any>(`${this.api}/casos/${encodeURIComponent(id)}`, cambios, this.json));
}
